import { useCallback, useEffect, useRef, useState } from "react";
import { getSyncRun } from "../api/client.js";
import { ACTIVE_SYNC_STATUSES } from "../domain/order-status.js";

export function useManualSync(triggerSync, onCompleted) {
  const [syncRun, setSyncRun] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState("");
  const timerRef = useRef(null);
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const poll = useCallback(
    async (id) => {
      stopPolling();
      try {
        const payload = await getSyncRun(id);
        const nextRun = payload.syncRun;
        setSyncRun(nextRun);
        if (ACTIVE_SYNC_STATUSES.has(nextRun?.status)) {
          setSyncing(true);
          timerRef.current = window.setTimeout(() => poll(id), 1500);
          return;
        }
        setSyncing(false);
        await onCompletedRef.current?.(nextRun);
      } catch (error) {
        setSyncing(false);
        setSyncError(error.message);
      }
    },
    [stopPolling],
  );

  const startSync = useCallback(async () => {
    setSyncing(true);
    setSyncError("");
    try {
      const payload = await triggerSync();
      setSyncRun(payload.syncRun);
      await poll(payload.syncRun.id);
    } catch (error) {
      setSyncing(false);
      setSyncError(error.message);
    }
  }, [poll, triggerSync]);

  useEffect(() => {
    if (
      syncRun?.id &&
      ACTIVE_SYNC_STATUSES.has(syncRun.status) &&
      !syncing
    ) {
      setSyncing(true);
      poll(syncRun.id);
    }
  }, [poll, syncRun?.id, syncRun?.status, syncing]);

  useEffect(() => stopPolling, [stopPolling]);

  return {
    syncRun,
    setSyncRun,
    syncing,
    syncError,
    startSync,
  };
}
